/**
 * The office daily log (PF-2, fieldiq-api#1200), a full page:
 *   /pipeline-facility/jobs/:jobId/daily-logs/new  — write one (POST)
 *   /pipeline-facility/daily-logs/:id              — read, edit (PATCH), delete
 *
 * The read shows crew hours and per-unit hours, the hour-meter end on OWNED
 * units only (a rent-in is not metered here), each unit's linked pre-use check,
 * and the totals the API returns (nothing is summed on this side). Edit opens
 * the same form inline. A save that went through with a `warnings` entry
 * (meter_below_previous) says so in an amber notice, never silently.
 *
 * The work date defaults to `meta.today` — the tenant's today, from the API —
 * and a later day is not offered (the API's future_day). Writing is PF_WORK;
 * a field hand must also be on the crew (403 not_on_crew reads as a refusal).
 * Deleting is PF_DISPATCH. A completed or cancelled job's log is read-only.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Clock, Pencil, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import {
  useCreatePfDailyLog,
  useDeletePfDailyLog,
  usePfDailyLog,
  usePfJob,
  usePfPreUseChecks,
  useUpdatePfDailyLog,
} from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchPf,
  canWorkPf,
  fmtNumber,
  pfApiError,
  pfDay,
  pfJobLoggable,
  pfRefusal,
  pfWarningText,
} from '../../lib/pipeline-facility';
import type { PfDailyLog, PfLogWarning } from '../../types/pipeline-facility';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { chainageRange } from './JobsScreen';
import { LogEditor } from './LogEditor';
import { officeLogOptions, officeLogValues } from './logOptions';
import {
  isNotFound,
  PF_DATE_INPUT,
  PF_EYEBROW,
  PF_PRIMARY,
  PF_SECONDARY,
  PfDialog,
  PfEmpty,
  PfField,
  PfFormError,
  PfLoading,
  PfPanel,
  type PfColumn,
  PfRegister,
} from './PfBits';

export function LogWarnings({ warnings, unitName }: { warnings: PfLogWarning[]; unitName: (equipmentId?: string) => string | null }) {
  if (!warnings.length) return null;
  return (
    <div role="status" data-testid="pf-log-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
      <ul className="space-y-1">
        {warnings.map((w, i) => (
          <li key={i}>{pfWarningText(w, unitName(w.equipmentId))}</li>
        ))}
      </ul>
    </div>
  );
}

type UnitLine = PfDailyLog['equipment'][number];
type CrewLine = PfDailyLog['crew'][number];
const SETTLED = { isLoading: false };

export function LogReadout({ log, chainageUnit }: { log: PfDailyLog; chainageUnit: string | null }) {
  const crewColumns: PfColumn<CrewLine>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span> },
    { key: 'hours', header: 'Hours', align: 'right', render: (c) => fmtNumber(c.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (c) => fmtNumber(c.standbyHours) },
    { key: 'notes', header: 'Notes', render: (c) => c.notes || '—' },
  ];
  const unitColumns: PfColumn<UnitLine>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (e) => (
        <>
          <p className="font-extrabold text-white">{e.unitName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{e.kind === 'rent_in' ? `Rent-in · ${e.vendorName ?? '—'}` : `Owned · ${e.unitNumber ?? '—'}`}</p>
        </>
      ),
    },
    { key: 'hours', header: 'Hours', align: 'right', render: (e) => fmtNumber(e.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (e) => fmtNumber(e.standbyHours) },
    { key: 'meter', header: 'Hour meter end', align: 'right', render: (e) => (e.kind === 'owned' ? fmtNumber(e.hourMeterEnd) : '—') },
    {
      key: 'check',
      header: 'Pre-use check',
      render: (e) => (e.check ? `${(e.check.status ?? '—').replace(/_/g, ' ')} · ${e.check.checkedBy ?? '—'}` : '—'),
    },
    { key: 'notes', header: 'Notes', render: (e) => e.notes || '—' },
  ];
  return (
    <>
      <PfPanel eyebrow="Daily log" title="The day" testId="pf-log-header">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div><dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Weather</dt><dd className="mt-1 text-sm font-semibold text-white">{log.weather || '—'}</dd></div>
          <div><dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Progress{chainageUnit ? ` (${chainageUnit})` : ''}</dt><dd className="mt-1 font-mono text-sm font-semibold text-white">{chainageRange(log.progressFromChainage, log.progressToChainage, null)}</dd></div>
          <div><dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Entered by</dt><dd className="mt-1 text-sm font-semibold text-white">{log.createdByName || '—'}</dd></div>
          <div><dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Last changed by</dt><dd className="mt-1 text-sm font-semibold text-white">{log.updatedByName || '—'}</dd></div>
        </dl>
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Work done: </span>{log.narrative || '—'}</p>
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text"><span className="font-bold">Safety: </span>{log.safetyNotes || '—'}</p>
      </PfPanel>
      <PfPanel eyebrow="Crew" title="Crew hours" testId="pf-log-crew-read">
        <PfRegister query={SETTLED} rows={log.crew} columns={crewColumns} rail={() => 'border-l-fi-steel'} subject="crew hours" testId="pf-log-crew-row" minWidth="min-w-[640px]"
          empty={<PfEmpty>No crew hours on this log.</PfEmpty>}
          card={(c) => <p className="text-sm text-fieldiq-text"><span className="font-extrabold text-white">{c.userName || '—'}</span> · {fmtNumber(c.hours)} h · standby {fmtNumber(c.standbyHours)} h</p>} />
      </PfPanel>
      <PfPanel eyebrow="Equipment" title="Unit hours" testId="pf-log-units-read">
        <PfRegister query={SETTLED} rows={log.equipment} columns={unitColumns} rail={(e) => (e.kind === 'rent_in' ? 'border-l-fi-blue' : 'border-l-fi-steel')} subject="unit hours" testId="pf-log-unit-row" minWidth="min-w-[900px]"
          empty={<PfEmpty>No unit hours on this log.</PfEmpty>}
          card={(e) => (
            <p className="text-sm text-fieldiq-text">
              <span className="font-extrabold text-white">{e.unitName || '—'}</span> · {fmtNumber(e.hours)} h
              {e.kind === 'owned' ? ` · meter ${fmtNumber(e.hourMeterEnd)}` : ' · rent-in'}
              {` · check ${e.check ? (e.check.status ?? '—').replace(/_/g, ' ') : '—'}`}
            </p>
          )} />
      </PfPanel>
    </>
  );
}

export default function PfDailyLogScreen() {
  const { id, jobId: jobIdParam } = useParams<{ id?: string; jobId?: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const logQuery = usePfDailyLog(id);
  const log = logQuery.data?.data ?? null;
  const jobId = jobIdParam ?? log?.jobId;
  const jobQuery = usePfJob(jobId);
  const job = jobQuery.data?.data ?? null;
  const meta = jobQuery.data?.meta ?? logQuery.data?.meta ?? null;
  const today = meta?.today ?? null;
  const [workDate, setWorkDate] = useState<string>('');
  const day = isNew ? workDate || today || '' : log?.workDate ?? '';
  const checks = usePfPreUseChecks(jobId, day || undefined);
  const back = useBackToList(jobId ? `/pipeline-facility/jobs/${jobId}?tab=logs` : '/pipeline-facility/jobs');
  const create = useCreatePfDailyLog();
  const update = useUpdatePfDailyLog();
  const del = useDeletePfDailyLog();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<PfLogWarning[]>(() => ((location.state as { warnings?: PfLogWarning[] } | null)?.warnings ?? []));
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);

  const role = user?.role;
  const loggable = pfJobLoggable(job?.status);
  const canWrite = canWorkPf(role) && loggable;
  const canDelete = canDispatchPf(role) && loggable;
  const options = job ? officeLogOptions(job) : { crew: [], units: [] };
  const unitName = (equipmentId?: string) =>
    (equipmentId && options.units.find((u) => u.equipmentId === equipmentId && u.kind === 'owned')?.name) || null;
  const chainageUnit = meta?.units?.chainage ?? null;

  const primary = isNew ? jobQuery : logQuery;
  const failure = queryLoadFailure(primary) ?? (isNew ? null : queryLoadFailure(jobQuery));
  const notFound = isNotFound(queryFailureError(primary));
  const loading = primary.isLoading || (!!jobId && jobQuery.isLoading);

  const onSaveError = (err: unknown, fallback: string) => {
    const e = pfApiError(err);
    setExistingId(e.code === 'daily_log_exists' && typeof e.extra.existingId === 'string' ? e.extra.existingId : null);
    setError(pfRefusal(err, fallback));
  };

  return (
    <div data-testid="pf-daily-log-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={job ? `${PF_EYEBROW} · ${job.jobNumber}` : PF_EYEBROW}
        title={isNew ? 'New daily log' : `Daily log · ${pfDay(log?.workDate)}`}
        description={job ? `${job.customerName ?? '—'} · ${job.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          !isNew && log && (canWrite || canDelete) ? (
            <div className="flex flex-wrap gap-2">
              {canWrite && !editing && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setError(null); setEditing(true); setFormKey((k) => k + 1); }}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit log
                </button>
              )}
              {canDelete && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={
          isNew
            ? undefined
            : [
                { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: 'Work date', tone: 'neutral', loading: logQuery.isLoading, value: pfDay(log?.workDate) },
                { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Crew hours', sub: log ? `Standby ${fmtNumber(log.totals.crewStandbyHours)}` : undefined, tone: 'neutral', loading: logQuery.isLoading, value: fmtNumber(log?.totals.crewHours) },
                { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Unit hours', sub: log ? `Rent-in ${fmtNumber(log.totals.rentInHours)}` : undefined, tone: 'neutral', loading: logQuery.isLoading, value: fmtNumber(log?.totals.ownedEquipmentHours) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <PfLoading label="Loading daily log" />
          ) : notFound || (!failure && (isNew ? !job : !log)) ? (
            <PfPanel eyebrow="Daily log" title="Daily log">
              <PfEmpty>{isNew ? 'This job was not found.' : 'This daily log was not found. It may have been deleted.'}</PfEmpty>
            </PfPanel>
          ) : failure ? (
            <PfPanel eyebrow="Daily log" title="Daily log">
              <LoadFailureNotice error={queryFailureError(queryLoadFailure(primary) ? primary : jobQuery)} kind={failure} subject="this daily log" />
            </PfPanel>
          ) : (
            <>
              <LogWarnings warnings={warnings} unitName={unitName} />
              {job && !loggable && (
                <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="pf-log-locked">
                  {job.status === 'draft'
                    ? 'This job is still a draft. Schedule it before logging work on it.'
                    : 'This job is completed or cancelled, so its logs can no longer be changed.'}
                </p>
              )}
              {isNew ? (
                !canWorkPf(role) ? (
                  <PfPanel eyebrow="Daily log" title="New daily log">
                    <PfEmpty>Your role cannot write a daily log.</PfEmpty>
                  </PfPanel>
                ) : loggable && job ? (
                  <PfPanel eyebrow="Daily log" title="New daily log" testId="pf-log-new">
                    <div className="space-y-4 px-4 py-4">
                      <div className="max-w-xs">
                        <PfField label="Work date" hint={today ? `Today is ${pfDay(today)}` : undefined}>
                          <input
                            type="date"
                            className={PF_DATE_INPUT}
                            aria-label="Work date"
                            value={day}
                            max={today ?? undefined}
                            onChange={(e) => { setWorkDate(e.target.value); setFormKey((k) => k + 1); }}
                          />
                        </PfField>
                      </div>
                      {existingId && (
                        <Link className="text-sm font-bold text-fi-amber hover:underline" to={`/pipeline-facility/daily-logs/${existingId}`} replace>
                          Open the log for that day
                        </Link>
                      )}
                      {day && (
                        <LogEditor
                          key={`${day}-${formKey}`}
                          workDate={day}
                          values={null}
                          crewOptions={options.crew}
                          unitOptions={options.units}
                          checks={checks.data?.data ?? []}
                          chainageUnit={chainageUnit}
                          pending={create.isPending}
                          error={error}
                          submitLabel="Save daily log"
                          onCancel={back}
                          onSubmit={(body) => {
                            setError(null);
                            create.mutate(
                              { jobId: job.id, body: { ...body, work_date: day } },
                              {
                                onSuccess: (res) => {
                                  toast.success('Daily log saved');
                                  // Set here as well as in the navigation state: the two
                                  // routes render this same screen, so React may keep this
                                  // instance (and its state) rather than mount a new one.
                                  setWarnings(res.warnings);
                                  if (res.data?.id)
                                    navigate(`/pipeline-facility/daily-logs/${res.data.id}`, { replace: true, state: { warnings: res.warnings } });
                                },
                                onError: (err) => onSaveError(err, 'The daily log could not be saved.'),
                              },
                            );
                          }}
                        />
                      )}
                    </div>
                  </PfPanel>
                ) : null
              ) : log ? (
                editing && canWrite ? (
                  <PfPanel eyebrow="Daily log" title="Edit daily log" testId="pf-log-edit">
                    <div className="px-4 py-4">
                      <LogEditor
                        key={formKey}
                        workDate={log.workDate}
                        values={officeLogValues(log)}
                        crewOptions={options.crew}
                        unitOptions={options.units}
                        checks={checks.data?.data ?? []}
                        chainageUnit={chainageUnit}
                        pending={update.isPending}
                        error={error}
                        submitLabel="Save daily log"
                        onCancel={() => setEditing(false)}
                        onSubmit={(body) => {
                          setError(null);
                          update.mutate(
                            { id: log.id, body },
                            {
                              onSuccess: (res) => {
                                toast.success('Daily log saved');
                                setWarnings(res.warnings);
                                setEditing(false);
                              },
                              onError: (err) => onSaveError(err, 'The daily log could not be saved.'),
                            },
                          );
                        }}
                      />
                    </div>
                  </PfPanel>
                ) : (
                  <LogReadout log={log} chainageUnit={chainageUnit} />
                )
              ) : null}
            </>
          )}
        </div>
      </section>

      {log && canDelete && (
        <PfDialog
          open={deleting}
          title="Delete daily log"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={PF_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep log
              </button>
              <button
                type="button"
                className={PF_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(log.id, {
                    onSuccess: () => {
                      toast.success('Daily log deleted');
                      navigate(`/pipeline-facility/jobs/${log.jobId}?tab=logs`, { replace: true });
                    },
                    onError: (err) => setDeleteError(pfRefusal(err, 'The daily log could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete log'}
              </button>
            </>
          }
        >
          <PfFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes the {pfDay(log.workDate)} log and the hour-meter readings it recorded.</p>
        </PfDialog>
      )}
    </div>
  );
}
